/* Customer account API hooks (TanStack Query).
 *
 * Endpoints (plan §"Backend/API Plan"):
 *   GET  /customer-account/me
 *   GET  /customer-account/donations?year=&cursor=&limit=
 *   GET  /customer-account/donations/summary?year=
 *   GET  /customer-account/payments?purposeType=&status=&cursor=&limit=
 *   POST /customer-account/donations/export   → CSV body + X-Export-* headers
 *
 * Pagination contract:
 *   - `cursor` is server-generated opaque token; clients pass it through.
 *   - default `limit=20`, max `limit=100`.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { ApiError } from '../../shared/api/ApiClient.js';
import { resolvePublicApiFailureMessage } from '../../shared/errors/publicApiFailureMessage.js';
import { useApiClient } from '../../shared/providers/AppDataProvider.js';
import { useRuntimeConfig } from '../../shared/providers/RuntimeConfigProvider.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { loadRuntimeConfig } from '../../config/runtimeConfig.js';
import {
  ACCOUNT_ME_STALE_MS,
  MISSING_CUSTOMER_SESSION_CREDENTIAL_MESSAGE,
} from '../../shared/session/fetchPlatformMe.js';
import { reconcileEstablishedPlatformMe } from '../../shared/session/establishedMeValidation.js';
import { isPlatformChromeTenant } from '../../app/platformChromeTenant.js';
import {
  DEFAULT_PAGE_LIMIT,
  MAX_PAGE_LIMIT,
  type CursorPage,
  type CustomerMeDTO,
  type CustomerMembershipsDTO,
  type DonationContributionDTO,
  type DonationsSummaryDTO,
  type PaymentHistoryItemDTO,
  type ReceiptLocalePreferenceDTO,
  type ReceiptLocalePreferencePutBody,
  type EmailLocalePreferenceDTO,
  type EmailLocalePreferencePutBody,
  type CustomerRefreshSessionsDTO,
  type RevokeCustomerRefreshSessionDTO,
} from './accountTypes.js';
import { assertStepUpVerified } from '../../shared/session/customerStepUp.js';
import { resolveCustomerCredentialSend } from '../../shared/session/resolveCustomerCredentialSend.js';
import { stormAwareFetch } from '../../shared/http/stormAwareFetch.js';
import { fetchPlatformAccountJson } from './platformAccountFetch.js';
import { normalizeCustomerMembershipsDTO } from './customerMembershipsNormalize.js';
import {
  orgDirectoryListRefetchInterval,
  orgDirectoryStaleTime,
} from '../tenant-select/hooks/useOrgDirectoryRealtimeSync.js';

export { fetchPlatformAccountJson } from './platformAccountFetch.js';
export {
  normalizeCustomerMembershipItem,
  normalizeCustomerMembershipsDTO,
} from './customerMembershipsNormalize.js';

export {
  downloadAccountReceiptPdf,
  triggerBlobDownload,
  canDownloadInternalReceipt,
  receiptDownloadFilename,
} from './accountReceiptDownload.js';

const ACCOUNT_QUERY_OPTIONS = {
  staleTime: ACCOUNT_ME_STALE_MS,
  refetchOnMount: false as const,
  refetchOnWindowFocus: false as const,
};

/**
 * G6 / G12 — when orders (or another surface) mounts org-directory SSE and
 * passes `directorySseConnected`, memberships use /orgs-class staleTime +
 * poll-when-down. Omit for default ACCOUNT_QUERY_OPTIONS (home/payments/etc.).
 */
export type AccountMembershipsRealtimeOptions = {
  readonly directorySseConnected: boolean;
};

function membershipsQueryOptions(
  realtime: AccountMembershipsRealtimeOptions | undefined,
): {
  staleTime: number;
  refetchOnMount: false;
  refetchOnWindowFocus: boolean;
  refetchInterval?: number | false;
} {
  if (realtime === undefined) {
    return ACCOUNT_QUERY_OPTIONS;
  }
  return {
    staleTime: orgDirectoryStaleTime(realtime.directorySseConnected),
    refetchInterval: orgDirectoryListRefetchInterval(realtime.directorySseConnected),
    refetchOnMount: false,
    // G7 — /orgs-class tab-return parity when directory SSE wiring is active.
    refetchOnWindowFocus: true,
  };
}

/** V2 — session present after establish or `/me` 200. */
export function isAccountAccessUsable(
  session: { readonly customerId?: number } | null | undefined,
): boolean {
  return session?.customerId !== undefined;
}

/** Tenant-scoped account GET enablement (URL tenant required). */
export function isAccountQueryEnabled(
  session: { readonly customerId?: number } | null | undefined,
  tenantCode: string,
  extra = true,
): boolean {
  return (
    extra &&
    session?.customerId !== undefined &&
    isAccountAccessUsable(session) &&
    !isPlatformChromeTenant(tenantCode)
  );
}

function clampLimit(limit: number | undefined): number {
  if (limit === undefined || Number.isNaN(limit)) {
    return DEFAULT_PAGE_LIMIT;
  }
  if (limit < 1) {
    return DEFAULT_PAGE_LIMIT;
  }
  return Math.min(limit, MAX_PAGE_LIMIT);
}

export function useAccountMemberships(
  enabled = true,
  realtime?: AccountMembershipsRealtimeOptions,
): UseQueryResult<CustomerMembershipsDTO, Error> {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const customerId = session?.customerId;
  return useQuery({
    queryKey: customerQueryKeys.accountMemberships(tenantCode, customerId ?? -1),
    queryFn: async () =>
      normalizeCustomerMembershipsDTO(
        await api.request<unknown>('/customer-account/memberships', {
          // Branding logoUrl — avoid HTTP cache after Vzhled (/orgs public-tenants parity).
          cache: 'no-store',
        }),
      ),
    enabled: isAccountQueryEnabled(session, tenantCode, enabled),
    ...membershipsQueryOptions(realtime),
  });
}

/** Canonical platform chrome `/me` — sibling of leave / activate-staged / contacts. */
export const PLATFORM_ACCOUNT_ME_PATH = '/api/v1/customer-account/me';
/** Wave 5 — absolute memberships list for OIDC Account link tenant pick. */
export const PLATFORM_ACCOUNT_MEMBERSHIPS_PATH =
  '/api/v1/customer-account/memberships';
/** Wave E — absolute PRODUCT payments list (CURSOR). */
export const PLATFORM_ACCOUNT_PAYMENTS_PATH = '/api/v1/customer-account/payments';
/** Wave E — absolute donations list (CURSOR) on platform `/account/donations`. */
export const PLATFORM_ACCOUNT_DONATIONS_PATH = '/api/v1/customer-account/donations';
/** Wave E — absolute donations summary on platform `/account/donations`. */
export const PLATFORM_ACCOUNT_DONATIONS_SUMMARY_PATH =
  '/api/v1/customer-account/donations/summary';

/**
 * Platform chrome memberships — absolute `/api/v1/customer-account/memberships`
 * with credentials (leave / activate-staged pattern). Never useApiClient on
 * `__platform__` (throws PLATFORM_CHROME_NO_API_TENANT).
 */
export function usePlatformAccountMemberships(
  enabled = true,
  realtime?: AccountMembershipsRealtimeOptions,
): UseQueryResult<CustomerMembershipsDTO, Error> {
  const config = useRuntimeConfig();
  const { session } = useCustomerSession();
  const customerId = session?.customerId;
  const accessUsable = isAccountAccessUsable(session);
  return useQuery({
    queryKey: customerQueryKeys.platformAccountMemberships(customerId ?? -1),
    queryFn: async ({ signal }) =>
      normalizeCustomerMembershipsDTO(
        await fetchPlatformAccountJson<unknown>({
          apiBaseUrl: config.apiBaseUrl,
          path: PLATFORM_ACCOUNT_MEMBERSHIPS_PATH,
          accessToken: null,
          signal,
          // Branding logoUrl — /orgs public-tenants parity.
          cache: 'no-store',
        }),
      ),
    enabled: enabled && customerId !== undefined && accessUsable,
    ...membershipsQueryOptions(realtime),
  });
}

/**
 * Absolute `GET /api/v1/customer-account/me` — NOT tenant ApiClient.
 * G6 — routes through {@link reconcileEstablishedPlatformMe} (shared `/me`
 * single-flight + ceiling). Established SPA rotates dying access before 401.
 * Abort abandons the wait only — never frees ceiling quota.
 */
export async function fetchPlatformAccountMe(input: {
  apiBaseUrl: string;
  /**
   * @deprecated Spec A — ignored; cookie jar is always used (parity with
   * `fetchPlatformAccountJson` / AppDataProvider ApiClient).
   */
  accessToken?: string | null;
  signal?: AbortSignal;
}): Promise<CustomerMeDTO> {
  void input.accessToken;
  const runtime = loadRuntimeConfig();
  const reconciled = await reconcileEstablishedPlatformMe(
    { ...runtime, apiBaseUrl: input.apiBaseUrl },
    {
      hasMemorySession: true,
      ...(input.signal !== undefined ? { signal: input.signal } : {}),
    },
  );
  switch (reconciled.action) {
    case 'ok':
      return reconciled.me;
    case 'keep':
      throw new ApiError(
        'Customer /me temporarily unavailable. Retry shortly.',
        503,
        'ME_TRANSIENT',
      );
    case 'end':
      throw new ApiError(
        MISSING_CUSTOMER_SESSION_CREDENTIAL_MESSAGE,
        401,
        'UNAUTHORIZED',
      );
    default: {
      const _exhaustive: never = reconciled;
      return _exhaustive;
    }
  }
}

/**
 * Continuous hub `/me` — always platform credentials `/me` via
 * {@link fetchPlatformAccountMe} / {@link reconcileEstablishedPlatformMe} (G6: no tenant
 * ApiClient ceiling-bypass path). Memberships stay on tenant ApiClient.
 *
 * `freshGate` (AccountSessionValidator): never trust a stale RQ success while
 * cookies may already be gone — always refetch on mount so orders/payments
 * cannot stampede 401 behind a cached `/me` 200.
 */
export function useAccountMe(
  enabled = true,
  options?: { readonly freshGate?: boolean },
): UseQueryResult<CustomerMeDTO, Error> {
  const config = useRuntimeConfig();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const customerId = session?.customerId;
  const accessUsable = isAccountAccessUsable(session);
  const freshGate = options?.freshGate === true;
  return useQuery({
    queryKey: customerQueryKeys.accountMe(tenantCode, customerId ?? -1),
    queryFn: ({ signal }) =>
      fetchPlatformAccountMe({
        apiBaseUrl: config.apiBaseUrl,
        accessToken: null,
        signal,
      }),
    // Identity-only hub / jwt-null: still fetch when session is present.
    enabled: enabled && customerId !== undefined && accessUsable,
    ...(freshGate
      ? {
          staleTime: 0,
          refetchOnMount: 'always' as const,
          refetchOnWindowFocus: false,
        }
      : ACCOUNT_QUERY_OPTIONS),
  });
}

export interface DonationsListFilters {
  year?: number;
  cursor?: string | null;
  limit?: number;
}

export function useAccountDonations(
  filters: DonationsListFilters,
  enabled = true
): UseQueryResult<CursorPage<DonationContributionDTO>, Error> {
  const api = useApiClient();
  const config = useRuntimeConfig();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const customerId = session?.customerId;
  const accessUsable = isAccountAccessUsable(session);
  const platformChrome = isPlatformChromeTenant(tenantCode);
  const limit = clampLimit(filters.limit);
  const queryFilters: DonationsListFilters = {
    limit,
    ...(filters.year !== undefined ? { year: filters.year } : {}),
    ...(filters.cursor !== undefined && filters.cursor !== null
      ? { cursor: filters.cursor }
      : {}),
  };
  return useQuery({
    queryKey: customerQueryKeys.accountDonations(
      tenantCode,
      customerId ?? -1,
      queryFilters
    ),
    queryFn: ({ signal }) => {
      const query = {
        ...(filters.year !== undefined ? { year: filters.year } : {}),
        ...(filters.cursor !== undefined && filters.cursor !== null
          ? { cursor: filters.cursor }
          : {}),
        limit,
      };
      if (platformChrome) {
        return fetchPlatformAccountJson<CursorPage<DonationContributionDTO>>({
          apiBaseUrl: config.apiBaseUrl,
          path: PLATFORM_ACCOUNT_DONATIONS_PATH,
          accessToken: null,
          signal,
          query,
        });
      }
      return api.request<CursorPage<DonationContributionDTO>>(
        '/customer-account/donations',
        { query },
      );
    },
    enabled:
      enabled &&
      customerId !== undefined &&
      accessUsable &&
      (platformChrome || !isPlatformChromeTenant(tenantCode)),
    ...ACCOUNT_QUERY_OPTIONS,
  });
}

export function useAccountDonationsSummary(
  year?: number,
  enabled = true
): UseQueryResult<DonationsSummaryDTO, Error> {
  const api = useApiClient();
  const config = useRuntimeConfig();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const customerId = session?.customerId;
  const accessUsable = isAccountAccessUsable(session);
  const platformChrome = isPlatformChromeTenant(tenantCode);
  return useQuery({
    queryKey: customerQueryKeys.accountDonationsSummary(
      tenantCode,
      customerId ?? -1,
      year
    ),
    queryFn: ({ signal }) => {
      const query = year !== undefined ? { year } : {};
      if (platformChrome) {
        return fetchPlatformAccountJson<DonationsSummaryDTO>({
          apiBaseUrl: config.apiBaseUrl,
          path: PLATFORM_ACCOUNT_DONATIONS_SUMMARY_PATH,
          accessToken: null,
          signal,
          query,
        });
      }
      return api.request<DonationsSummaryDTO>('/customer-account/donations/summary', {
        query,
      });
    },
    enabled:
      enabled &&
      customerId !== undefined &&
      accessUsable &&
      (platformChrome || !isPlatformChromeTenant(tenantCode)),
    ...ACCOUNT_QUERY_OPTIONS,
  });
}

export interface PaymentsListFilters {
  purposeType?: 'PRODUCT' | 'DONATION';
  status?: string;
  cursor?: string | null;
  limit?: number;
}

export interface PaymentClaimDTO {
  claimId: string;
  bankInboundTransactionId: string;
  variableSymbol: string;
  amountMinor: number;
  paidAt: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  note: string | null;
  submittedAt: string;
  reviewedAt: string | null;
  reviewNote: string | null;
  transactionId: number | null;
  paymentId: string | null;
}

export interface RecurringDonationSetupDTO {
  setupId: string;
  donationProjectId: number | null;
  variableSymbol: string;
  specificSymbol: string | null;
  constantSymbol: string | null;
  amountMinor: number;
  currency: string;
  startAt: string;
  nextDueAt: string;
  missedPeriodCount: number;
  status: 'ACTIVE' | 'PAUSED' | 'CANCELLED';
}

/**
 * Walk CURSOR payments pages until a row matches `paymentId` and/or
 * `transactionId` (prefer paymentId). Same transport as {@link useAccountPayments}.
 * Caps pages to avoid unbounded loops on a pathological cursor stream.
 */
const ACCOUNT_PAYMENT_LOOKUP_MAX_PAGES = 50;

export async function fetchAccountPaymentByPaymentId(input: {
  readonly apiBaseUrl: string;
  readonly platformChrome: boolean;
  readonly api: {
    request: <T>(
      path: string,
      opts?: {
        query?: Record<string, string | number | boolean | undefined>;
        signal?: AbortSignal;
      },
    ) => Promise<T>;
  };
  readonly paymentId?: string | null;
  readonly transactionId?: number | null;
  readonly signal?: AbortSignal;
}): Promise<PaymentHistoryItemDTO | null> {
  const paymentId =
    input.paymentId !== undefined &&
    input.paymentId !== null &&
    input.paymentId.trim().length > 0
      ? input.paymentId.trim()
      : null;
  const transactionId =
    input.transactionId !== undefined &&
    input.transactionId !== null &&
    Number.isFinite(input.transactionId)
      ? input.transactionId
      : null;
  if (paymentId === null && transactionId === null) {
    return null;
  }

  const limit = MAX_PAGE_LIMIT;
  let cursor: string | null = null;
  for (let page = 0; page < ACCOUNT_PAYMENT_LOOKUP_MAX_PAGES; page += 1) {
    const query: Record<string, string | number | undefined> = {
      limit,
      ...(cursor !== null ? { cursor } : {}),
    };
    const result = input.platformChrome
      ? await fetchPlatformAccountJson<CursorPage<PaymentHistoryItemDTO>>({
          apiBaseUrl: input.apiBaseUrl,
          path: PLATFORM_ACCOUNT_PAYMENTS_PATH,
          accessToken: null,
          ...(input.signal !== undefined ? { signal: input.signal } : {}),
          query,
        })
      : await input.api.request<CursorPage<PaymentHistoryItemDTO>>(
          '/customer-account/payments',
          {
            query,
            ...(input.signal !== undefined ? { signal: input.signal } : {}),
          },
        );

    const byPaymentId =
      paymentId !== null
        ? result.items.find((item) => item.paymentId === paymentId)
        : undefined;
    if (byPaymentId !== undefined) {
      return byPaymentId;
    }
    const byTransactionId =
      transactionId !== null
        ? result.items.find((item) => item.transactionId === transactionId)
        : undefined;
    if (byTransactionId !== undefined) {
      return byTransactionId;
    }

    if (
      result.nextCursor === null ||
      result.nextCursor.length === 0 ||
      result.nextCursor === cursor
    ) {
      return null;
    }
    cursor = result.nextCursor;
  }
  return null;
}

export function useAccountPayments(
  filters: PaymentsListFilters,
  enabled = true
): UseQueryResult<CursorPage<PaymentHistoryItemDTO>, Error> {
  const api = useApiClient();
  const config = useRuntimeConfig();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const customerId = session?.customerId;
  const accessUsable = isAccountAccessUsable(session);
  const limit = clampLimit(filters.limit);
  const platformChrome = isPlatformChromeTenant(tenantCode);
  const queryFilters: PaymentsListFilters = {
    limit,
    ...(filters.purposeType !== undefined
      ? { purposeType: filters.purposeType }
      : {}),
    ...(filters.status !== undefined ? { status: filters.status } : {}),
    ...(filters.cursor !== undefined && filters.cursor !== null
      ? { cursor: filters.cursor }
      : {}),
  };
  return useQuery({
    queryKey: customerQueryKeys.accountPayments(customerId ?? -1, queryFilters),
    queryFn: ({ signal }) => {
      const query = {
        ...(filters.purposeType !== undefined
          ? { purposeType: filters.purposeType }
          : {}),
        ...(filters.status !== undefined ? { status: filters.status } : {}),
        ...(filters.cursor !== undefined && filters.cursor !== null
          ? { cursor: filters.cursor }
          : {}),
        limit,
      };
      // Wave E: platform hub uses absolute CURSOR payments (NONE-safe).
      if (platformChrome) {
        return fetchPlatformAccountJson<CursorPage<PaymentHistoryItemDTO>>({
          apiBaseUrl: config.apiBaseUrl,
          path: PLATFORM_ACCOUNT_PAYMENTS_PATH,
          // Spec A: ignored by helper — cookie jar always (parity with ApiClient).
          accessToken: null,
          signal,
          query,
        });
      }
      return api.request<CursorPage<PaymentHistoryItemDTO>>(
        '/customer-account/payments',
        { query },
      );
    },
    enabled: enabled && customerId !== undefined && accessUsable,
    ...ACCOUNT_QUERY_OPTIONS,
  });
}

export function useCancelAccountPayment(): UseMutationResult<
  { success: boolean; paymentId: string; status: string },
  Error,
  { paymentId: string; tenantCode?: string }
> {
  const api = useApiClient();
  const config = useRuntimeConfig();
  const queryClient = useQueryClient();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const customerId = session?.customerId;
  const platformChrome = isPlatformChromeTenant(tenantCode);

  return useMutation({
    mutationFn: async ({ paymentId, tenantCode: rowTenantCode }) => {
      // Wave E: platform ownership cancel OR switch-before-call to row tenant.
      if (platformChrome) {
        return fetchPlatformAccountJson<{
          success: boolean;
          paymentId: string;
          status: string;
        }>({
          apiBaseUrl: config.apiBaseUrl,
          path: `${PLATFORM_ACCOUNT_PAYMENTS_PATH}/${encodeURIComponent(paymentId)}/cancel`,
          method: 'POST',
          accessToken: null,
          body: {},
        });
      }
      void rowTenantCode;
      return api.request<{ success: boolean; paymentId: string; status: string }>(
        `/customer-account/payments/${encodeURIComponent(paymentId)}/cancel`,
        { method: 'POST', body: {} }
      );
    },
    onSuccess: async () => {
      if (customerId === undefined) {
        return;
      }
      // G17 — payments list only; never broad accountPrefix (refetches /me).
      await queryClient.invalidateQueries({
        queryKey: ['customer', tenantCode, 'account', customerId, 'payments'],
      });
    },
  });
}

export function useSubmitPaymentClaim(): UseMutationResult<
  PaymentClaimDTO,
  Error,
  { variableSymbol: string; amountMinor: number; claimedDate: string; note?: string }
> {
  const api = useApiClient();
  return useMutation({
    mutationFn: (body) =>
      api.request<PaymentClaimDTO>('/customer-account/payment-claims', {
        method: 'POST',
        body,
      }),
  });
}

export function useRecurringDonationSetups(
  enabled = true
): UseQueryResult<{ setups: RecurringDonationSetupDTO[] }, Error> {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const customerId = session?.customerId;
  return useQuery({
    queryKey: customerQueryKeys.accountRecurringSetups(tenantCode, customerId ?? -1),
    queryFn: () => api.request<{ setups: RecurringDonationSetupDTO[] }>('/customer-account/recurring-donations'),
    enabled: isAccountQueryEnabled(session, tenantCode, enabled),
    ...ACCOUNT_QUERY_OPTIONS,
  });
}

export function useCreateRecurringDonationSetup(): UseMutationResult<
  RecurringDonationSetupDTO,
  Error,
  {
    donationProjectId?: number | null;
    amountMinor: number;
    /** Already-allowed create field — server also defaults from tenant when omitted (G13). */
    currency?: string;
    startAt: string;
    /** Already-allowed create field — server also defaults from first active SP when omitted (G13). */
    salesPointId?: number;
  }
> {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const customerId = session?.customerId ?? -1;
  return useMutation({
    mutationFn: (body) =>
      api.request<RecurringDonationSetupDTO>('/customer-account/recurring-donations', {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: customerQueryKeys.accountRecurringSetups(tenantCode, customerId),
      });
    },
  });
}

export function useDeleteRecurringDonationSetup(): UseMutationResult<
  { deleted: boolean },
  Error,
  { setupId: string }
> {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const customerId = session?.customerId ?? -1;
  return useMutation({
    mutationFn: ({ setupId }) =>
      api.request<{ deleted: boolean }>(
        `/customer-account/recurring-donations/${encodeURIComponent(setupId)}`,
        { method: 'DELETE' }
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: customerQueryKeys.accountRecurringSetups(tenantCode, customerId),
      });
    },
  });
}

/**
 * Lifecycle-only PATCH for pause / resume.
 * Package E: do **not** send paid automation-contract fields
 * (`donationProjectId`, `amountMinor`, `currency`, `startAt`, `nextDueAt`) from the FE —
 * mixed lifecycle+paid payloads are treated as paid atomically on the server.
 * Resume (PAUSED→ACTIVE) is commercially gated; pause stays core after downgrade.
 */
export type RecurringDonationLifecyclePatchInput = {
  readonly setupId: string;
  readonly status: 'ACTIVE' | 'PAUSED';
};

export function useUpdateRecurringDonationSetup(): UseMutationResult<
  RecurringDonationSetupDTO,
  Error,
  RecurringDonationLifecyclePatchInput
> {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const customerId = session?.customerId ?? -1;
  return useMutation({
    mutationFn: ({ setupId, status }) =>
      api.request<RecurringDonationSetupDTO>(
        `/customer-account/recurring-donations/${encodeURIComponent(setupId)}`,
        // Lifecycle-only body — never widen with paid contract fields.
        { method: 'PATCH', body: { status } }
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: customerQueryKeys.accountRecurringSetups(tenantCode, customerId),
      });
    },
  });
}

export function useAccountReceiptLocales(
  enabled = true
): UseQueryResult<ReceiptLocalePreferenceDTO, Error> {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const customerId = session?.customerId;
  return useQuery({
    queryKey: customerQueryKeys.accountReceiptLocales(tenantCode, customerId ?? -1),
    queryFn: () =>
      api.request<ReceiptLocalePreferenceDTO>(
        '/customer-account/preferences/receipt-locales'
      ),
    // G6 Spec A parity with memberships — BAN firing while session is null
    // or access unusable (zombie chrome must not stampede receipt-locales).
    enabled: isAccountQueryEnabled(session, tenantCode, enabled),
    ...ACCOUNT_QUERY_OPTIONS,
  });
}

export function useUpdateAccountReceiptLocales(): UseMutationResult<
  ReceiptLocalePreferenceDTO,
  Error,
  ReceiptLocalePreferencePutBody
> {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ReceiptLocalePreferencePutBody) =>
      api.request<ReceiptLocalePreferenceDTO>(
        '/customer-account/preferences/receipt-locales',
        { method: 'PUT', body }
      ),
    onSuccess: () => {
      const customerId = session?.customerId;
      if (customerId === undefined) {
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: customerQueryKeys.accountReceiptLocales(tenantCode, customerId),
      });
    },
  });
}

export function useAccountEmailLocale(
  enabled = true
): UseQueryResult<EmailLocalePreferenceDTO, Error> {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const customerId = session?.customerId;
  return useQuery({
    queryKey: customerQueryKeys.accountEmailLocale(tenantCode, customerId ?? -1),
    queryFn: () =>
      api.request<EmailLocalePreferenceDTO>(
        '/customer-account/preferences/email-locale'
      ),
    enabled: isAccountQueryEnabled(session, tenantCode, enabled),
    ...ACCOUNT_QUERY_OPTIONS,
  });
}

export function useUpdateAccountEmailLocale(): UseMutationResult<
  EmailLocalePreferenceDTO,
  Error,
  EmailLocalePreferencePutBody
> {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: EmailLocalePreferencePutBody) =>
      api.request<EmailLocalePreferenceDTO>(
        '/customer-account/preferences/email-locale',
        { method: 'PUT', body }
      ),
    onSuccess: () => {
      const customerId = session?.customerId;
      if (customerId === undefined) {
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: customerQueryKeys.accountEmailLocale(tenantCode, customerId),
      });
    },
  });
}

/**
 * Manual donation export (interim, non-signed CSV).
 *
 * Plan §"Interim certificate/export endpoint": CSV body + X-Export-* headers.
 * This helper does NOT go through ApiClient because the response is not a
 * JSON envelope — it's a CSV stream with sidecar metadata in HTTP headers.
 * The caller receives both the parsed metadata and a Blob suitable for
 * `URL.createObjectURL()`-based downloads.
 */
export interface DonationExportResult {
  filename: string;
  blob: Blob;
  metadata: {
    isInterim: boolean;
    legalValidity: string;
    generatedAt: string;
    tenantCode: string;
    customerId: string;
  };
}

export interface DonationExportInput {
  year?: number;
  apiBaseUrl: string;
  tenantCode: string;
  accessToken: string;
  idempotencyKey?: string;
}

export async function requestDonationExport(
  input: DonationExportInput
): Promise<DonationExportResult> {
  assertStepUpVerified();
  const trimmedBase = input.apiBaseUrl.replace(/\/+$/, '');
  const url = `${trimmedBase}/api/${encodeURIComponent(
    input.tenantCode
  )}/v1/customer-account/donations/export`;

  const idempotencyKey =
    input.idempotencyKey ??
    (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `idem-${Date.now()}`);

  const credentialSend = resolveCustomerCredentialSend({
    bearerToken: input.accessToken.length > 0 ? input.accessToken : null,
  });
  const headers: Record<string, string> = {
    Accept: 'text/csv',
    'Content-Type': 'application/json',
    'Idempotency-Key': idempotencyKey,
  };
  if (credentialSend.authorizationHeader !== undefined) {
    headers.Authorization = credentialSend.authorizationHeader;
  }

  const res = await stormAwareFetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      ...(input.year !== undefined ? { year: input.year } : {}),
    }),
    credentials: credentialSend.credentials,
  });

  if (!res.ok) {
    let rawMessage: string | undefined;
    try {
      const body = (await res.json()) as { error?: string };
      if (typeof body.error === 'string' && body.error.length > 0) {
        rawMessage = body.error;
      }
    } catch {
      // ignore — use status fallback
    }
    throw new Error(
      resolvePublicApiFailureMessage({
        status: res.status,
        rawMessage,
      }),
    );
  }

  const blob = await res.blob();
  const dispositionHeader = res.headers.get('Content-Disposition') ?? '';
  const filenameMatch = /filename="?([^";]+)"?/i.exec(dispositionHeader);
  const filename = filenameMatch?.[1] ?? 'donation-export.csv';

  return {
    filename,
    blob,
    metadata: {
      isInterim: res.headers.get('X-Export-Is-Interim') === 'true',
      legalValidity: res.headers.get('X-Export-Legal-Validity') ?? '',
      generatedAt: res.headers.get('X-Export-Generated-At') ?? '',
      tenantCode: res.headers.get('X-Export-Tenant-Code') ?? '',
      customerId: res.headers.get('X-Export-Customer-Id') ?? '',
    },
  };
}

export interface CustomerSecuritySettingsDTO {
  otpStepUpEnabled: boolean;
}

export function useAccountSecuritySettings(
  enabled = true
): UseQueryResult<CustomerSecuritySettingsDTO, Error> {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const customerId = session?.customerId;
  return useQuery({
    queryKey: [...customerQueryKeys.accountMe(tenantCode, customerId ?? -1), 'security'],
    queryFn: () => api.request<CustomerSecuritySettingsDTO>('/customer-account/security'),
    enabled: isAccountQueryEnabled(session, tenantCode, enabled),
    ...ACCOUNT_QUERY_OPTIONS,
  });
}

export function useAccountSessions(
  enabled = true
): UseQueryResult<CustomerRefreshSessionsDTO, Error> {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const customerId = session?.customerId;
  return useQuery({
    queryKey: [...customerQueryKeys.accountMe(tenantCode, customerId ?? -1), 'sessions'],
    queryFn: () => api.request<CustomerRefreshSessionsDTO>('/customer-account/sessions'),
    enabled: isAccountQueryEnabled(session, tenantCode, enabled),
    ...ACCOUNT_QUERY_OPTIONS,
  });
}

export function useRevokeAccountSession(): UseMutationResult<
  RevokeCustomerRefreshSessionDTO,
  Error,
  { sessionId: string }
> {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const customerId = session?.customerId ?? -1;
  return useMutation({
    mutationFn: ({ sessionId }) =>
      api.request<RevokeCustomerRefreshSessionDTO>(
        `/customer-account/sessions/${encodeURIComponent(sessionId)}`,
        { method: 'DELETE' }
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...customerQueryKeys.accountMe(tenantCode, customerId), 'sessions'],
      });
    },
  });
}

export function useUpdateAccountSecuritySettings(): UseMutationResult<
  CustomerSecuritySettingsDTO,
  Error,
  { otpStepUpEnabled: boolean }
> {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const customerId = session?.customerId ?? -1;
  return useMutation({
    mutationFn: (body) =>
      api.request<CustomerSecuritySettingsDTO>('/customer-account/security', {
        method: 'PUT',
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...customerQueryKeys.accountMe(tenantCode, customerId), 'security'],
      });
    },
  });
}
